import { useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { imgWidth } from 'ugc/components/ugcGallery/montage/Montage';
import getCurrentMediaCacheData from 'client/utils/getCurrentMediaCacheData';
import { convertProxyImgUrl } from 'client/utils/getProxyImgUrl';
import useClientErrorHandler, { generateApiErrObj } from 'client/hooks/useClientErrorHandler';
import { getImgSizeBySrc } from 'utils/manipulateImg';
import { getMediaCacheTotalCounts, getMediaCacheData } from 'api/ugc';
import { IS_END } from 'config/promoOnlineStatus';
import * as clientErrTypes from 'config/clientErrorTypes';

export default function useMontageImgGenerator() {
    const errHandler = useClientErrorHandler();
    const { uuid: promoUUID, promoIdOfUgcLink: promoId, status: promoStatus } = useSelector(
        s => s.promo
    );
    const montageComponentRef = useRef(null);

    useEffect(() => {
        const initFlow = async () => {
            let isPlayable = true;
            try {
                // 因為活動結束後快取池的排程更新便不會再跑，因此會碰到快取為空但實際上有資料的情況發生，需要依靠前端觸發，拿快取資料時便會觸發後端建立快取池
                // 活動結束後快取池最大數量為 60，並且不管請求是多少張 或多或少，後端一律在沒有快取池的時候建立數量 60，若總資料小於 60 就依照現有多少建多少
                // 這邊在活動結束時要跑輪播牆一律都先請求一次各一張來觸發建立快取，目前先簡單做
                if (promoStatus === IS_END) {
                    const resp = await getMediaCacheData({
                        promoUUID,
                        firstNum: 1,
                        defaultNum: 1
                    });

                    if (resp.httpStatus !== 200) {
                        throw resp;
                    }
                }

                const totalCountsResp = await getMediaCacheTotalCounts({ promoUUID });
                if (totalCountsResp?.httpStatus !== 200) {
                    throw generateApiErrObj(totalCountsResp, clientErrTypes.UGC_GET_MEDIA_COUNTS);
                }
                mediaCacheCounts.first = totalCountsResp?.payload?.first ?? 0;
                mediaCacheCounts.default = totalCountsResp?.payload?.default ?? 0;
            } catch (err) {
                isPlayable = false;
                console.error(err);
                errHandler({ err });
            }

            if (isPlayable) {
                playImageMontage({
                    promoUUID,
                    promoId,
                    componentRef: montageComponentRef
                });
                document.addEventListener('visibilitychange', onVisibilityChangeHandler);
            }
        };

        if (Boolean(promoUUID) && Boolean(promoId)) {
            initFlow();
            return () =>
                document.removeEventListener('visibilitychange', onVisibilityChangeHandler);
        }
    }, [promoUUID, promoId, promoStatus, errHandler]);

    return montageComponentRef;
}

const mediaCacheCounts = {
    first: 0,
    default: 0
};
const currentPlayedCounts = {
    first: 0,
    default: 0
};
// 下次更新延遲
// const nextDelay = 10 * 1000;

let isFocusOnCurrentTab = true;
function onVisibilityChangeHandler() {
    if (document.visibilityState === 'visible') {
        isFocusOnCurrentTab = true;
    } else {
        isFocusOnCurrentTab = false;
    }
}
async function playImageMontage({ promoUUID, promoId, componentRef }) {
    function keepPlay() {
        playImageMontage({ promoUUID, promoId, componentRef });
        // setTimeout(
        //     () => playImageMontage({ promoUUID, promoId, componentRef }),
        //     componentRef.current.getMediasCount() * 100 + nextDelay
        // );
    }

    if (!isFocusOnCurrentTab) {
        return keepPlay();
    }

    const { data, nextPlayedCounts } = await getCurrentMediaCacheData({
        playedCounts: currentPlayedCounts,
        totalCounts: mediaCacheCounts,
        promoUUID,
        dataLength: componentRef.current.getMediasCount()
    });
    currentPlayedCounts.first = nextPlayedCounts.first;
    currentPlayedCounts.default = nextPlayedCounts.default;

    const photosTop = await Promise.all(
        data.first.map(({ value, serialNo }) =>
            getPhotosDataPayload({ type: 'top', promoId, url: value, serialNo })
        )
    );
    const photosDefault = await Promise.all(
        data.default.map(({ value, serialNo }) =>
            getPhotosDataPayload({ promoId, url: value, serialNo })
        )
    );
    componentRef.current.setPhotos({ type: 'default', tops: photosTop, defaults: photosDefault });

    // return keepPlay();
}

async function getPhotosDataPayload({ type, promoId, url, serialNo } = {}) {
    const src = convertProxyImgUrl({
        url,
        resizeWidth: imgWidth
    });
    return {
        key: `i_${type}_${serialNo}`,
        src,
        alt: serialNo,
        linkTo: `/ugc/${promoId}/${serialNo}${window.location.search}${window.location.hash}`,
        ...(await getImgSizeBySrc(src))
    };
}
